import { useTranslation } from 'react-i18next';
import styles from './../alert.module.scss';
import {
  auth,
  errorParser,
  FirebaseService,
  httpService,
  IconWrapper,
  REGEX,
} from '@flexo/general';
import { useEffect, useRef, useState } from 'react';
import Textfield from '../../textfield/textfield';
import Button from '../../button/button';
import { Player } from '@lottiefiles/react-lottie-player';
import { useDispatch, useSelector } from 'react-redux';
import {
  ALERT,
  KPI,
  METADATA,
  MODAL,
  RIGHT_DRAWER,
  USER,
} from '@flexo/reducers';
import { useNavigate } from 'react-router-dom';

const ENV = (import.meta as any).env;
const loader = '/loaderLottie.json';

export default function AlertConfirmCredentialsChanges() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const alertState = useSelector((state: any) => state.alert);
  const alertContent = t('banners.confirm_credentials_changes', {
    returnObjects: true,
  }) as any;

  const formRef = useRef<any>(null);
  const firebaseService = new FirebaseService();
  const [errorAlertContent, setErrorAlertContent] = useState<any>(
    t('alerts.wrong_request_data', { returnObjects: true })
  );
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);
  const [fieldVisible, setFieldVisible] = useState<boolean>(false);
  const [sendingForm, setSendingForm] = useState<boolean>(false);
  const [showPasswordReset, setShowPasswordReset] = useState<boolean>(false);
  const [passwordField, setPasswordField] = useState<string>('');
  const navigate = useNavigate();
  async function handleInputChange(event: any) {
    if (showAlert === true && formError === true) {
      setShowAlert(false);
      setShowPasswordReset(false);
    }

    setFormError(false);
    setPasswordField(event.target.value);
  }

  async function manageAlertCallBack() {
    let result: any = null;

    if (alertState?.alert?.callback) {
      result = await alertState.alert.callback();
    }

    if (typeof result === 'number' && result > 200) {
      setFormError(true);
    } else if (result === null) {
      dispatch(ALERT.ResetAlert({} as any));
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'ALERT_UNEXPECTED_ERROR',
          },
        } as any)
      );
    }
  }
  const onClickLogOut = async () => {
    // Close both modal and alert immediately
    dispatch(MODAL.CloseModal());
    dispatch(ALERT.ResetAlert({} as any));

    try {
      // First, set flag to redirect to forgot password before doing any logout operations
      // This ensures the flag is set before auth state changes
      localStorage.setItem('redirectToForgotPassword', 'true');

      console.log('_storage here', localStorage)

      // Clear app state
      dispatch(KPI.ResetKpiApiStore());
      dispatch(METADATA.ResetMetadata());
      dispatch(RIGHT_DRAWER.HideDrawer());

      // Signal that this is an intentional logout (for forgot password)
      // This helps prevent session expired alerts
      dispatch(USER.SetLogout());

      // Get token but don't show errors if it fails
      let IDtoken;
      try {
        IDtoken = await firebaseService.getIdToken();
      } catch (error) {
        // Ignore token errors during logout process
      }

      if (IDtoken) {
        // Sign out from Firebase and backend first
        await httpService
          .post({
            url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/signout`,
            idToken: IDtoken,
            data: {},
          })
          .catch(() => {
            // Ignore errors during signout
          });
      }

      // Sign out from Firebase regardless of whether we got a token
      await firebaseService.signOut().catch(() => {
        // Ignore signout errors
      });
    } catch (error) {
      // Continue even if there's an error with signout
    } finally {
      // Reset any pending alerts to prevent session expired messages
      dispatch(ALERT.ResetAlert({} as any));
    }
  };

  async function askForPasswordReset() {
    setPasswordField('');

    dispatch(ALERT.ResetAlert({} as any));
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'BANNER_CHECK_EMAIL',
          callback: alertState?.alert?.callback
            ? () => (alertState.alert as any).callback()
            : () => null,
          dismissCallback: alertState?.alert?.dismissCallback
            ? () => (alertState.alert as any).dismissCallback()
            : () => null,
        },
      } as any)
    );
  }

  async function checkPasswordValidity() {
    setSendingForm(true);

    const response = await httpService
      .post({
        url: `${ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN}`,
        data: {
          email:
            auth?.currentUser?.email ||
            user?.tokenContent?.firebaseUserResult?.email,
          password: passwordField,
          // tenant_id: ENV.VITE_APP_TENANT
        },
      })
      .then((resp: any) => ({
        status: resp.status,
        statusText: resp?.statusText || 'Unknown status text',
        message: resp?.data?.message || 'Unknown message',
      }))
      .catch((error) => {
        // console.warn(error);
        return {
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
          ...errorParser(error),
        };
      });

    if (response.status === 200) {
      if (alertState?.alert?.dismissCallback) {
        alertState?.alert?.dismissCallback();
      }
      await manageAlertCallBack();
    } else {
      // setShowAlert(true);
      setFormError(true);
      setShowPasswordReset(true);
    }

    setSendingForm(false);
  }

  function focusChild(event: any) {
    try {
      const parent = event.target;
      const child = parent.querySelector('input');
      child.focus();
    } catch (err: any) {
      console.warn(err);
    }
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const isAllowed = formError || !REGEX.password.test(passwordField);

        event.preventDefault();
        event.stopPropagation();

        if (!isAllowed && !sendingForm) {
          checkPasswordValidity();
        }
      }
    }

    // Aggiungi l'event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup per rimuovere l'event listener quando il componente si smonta o lo stato cambia
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [formError, passwordField, sendingForm]);

  return (
    <div>
      <div
        id="alert-confirm-credentials-changes"
        className={`${styles.Alert} ${styles.Alert__Confirm__Credentials__Changes}`}
      >
        <div className={styles.Alert__Title}>{alertContent.title}</div>
        <div className={styles.Alert__Content}>{alertContent.description}</div>
        <div className={`heading4 ${styles.Alert__Content__Label}`}>
          {alertContent.inputLabel}
        </div>
        <div className={styles.Alert__Content__Content}>
          <span className={styles.Alert__Content__Link} onClick={onClickLogOut}>
            {alertContent.forgotPassword}
          </span>
          <span> {alertContent.inputSubtitle}</span>
        </div>

        <form
          id="alert-confirm-credentials-changes-form"
          className={styles.Alert__Content__Form}
        >
          <div
            className={styles.Alert__Content__Form__Input}
            ref={formRef}
            onClick={(e) => focusChild(e)}
          >
            <Textfield
              label={''}
              name={alertContent.inputName}
              type={fieldVisible ? 'text' : 'password'}
              placeholder={alertContent.inputPlaceholder}
              onPaste={(e) => false}
              onCopy={(e) => false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="off"
              defaultValue={passwordField}
              onChange={(ev: any) => handleInputChange(ev)}
              icon={fieldVisible ? 'show' : 'hide'}
              onIconClick={() => setFieldVisible(!fieldVisible)}
              underline={formError === true ? 'visible' : 'hidden'}
              errorDescription={formError === true ? alertContent.error : ''}
              color="white"
            />
          </div>
        </form>

        <div className={styles.Alert__Footer__Actions}>
          <Button
            variant="secondary"
            color="grey"
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Cancel}`}
            label={alertContent.cancel}
            onClick={() => dispatch(ALERT.ResetAlert({} as any))}
          />

          <Button
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Confirm}`}
            color="primary"
            label={
              sendingForm ? (
                <Player
                  autoplay
                  loop
                  src={loader}
                  
                  style={{ height: 27, width: 80 }}
                />
              ) : (
                alertContent.confirm
              )
            }
            disabled={formError || !REGEX.password.test(passwordField) || sendingForm}
            onClick={() => checkPasswordValidity()}
          />
        </div>
      </div>
    </div>
  );
}
